import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "../services/api";
import { ChevronDown, Settings as SettingsIcon } from "lucide-react";
import AdminDocumentsView from '../components/AdminDocumentsView';
import '../components/AdminDocumentsView.css';
import TwoFactorSetup from '../components/TwoFactorSetup';

// lucide-react
import {
  BarChart2,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  ClipboardList,
  Trash2,
  Bell,
  Eye,
  Edit2,
  RotateCcw,
  CheckCircle,
  UserPlus,
  ArrowLeft,
} from "lucide-react";

// framer-motion (for <motion.div/>)
import { motion } from "framer-motion";

// recharts (SINGLE combined import — no duplicates anywhere else)
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
} from "recharts";



// ======= PDF & Assets (from OLD dashboard) =======
import { generatePDF as generatePDFUtil, generateTaskPDF as generateTaskPDFUtil } from "../utils/pdfGenerator";
import businessLogo from '../assets/business-logo.png';

/**
 * UnifiedAdminDashboard
 * ---------------------
 * New UI shell (collapsible sidebar, modern cards, charts)
 * + OLD dashboard's full DB-connected logic (staff, appraisals, PDF, delete, edit, reset pw)
 *
 * Drop-in replacement for your current AdminDashboard route/page.
 */
export default function UnifiedAdminDashboard({ onLogout } = {}) {
  const nav = useNavigate();

  // ======= NAV / LAYOUT STATE =======
  const [active, setActive] = useState("dashboard");
  const [selectedRegNumber, setSelectedRegNumber] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile
  const [desktopCollapsed, setDesktopCollapsed] = useState(false); // desktop mini-rail
  const [highlightedAppraisalId, setHighlightedAppraisalId] = useState(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);

  // Job Review State
  const [viewTask, setViewTask] = useState(null);
  const [reviewTask, setReviewTask] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: 'Approved', feedback: '' });

  const goTo = (section) => {
    setActive(section);
    setSidebarOpen(false);
  };

  // --- add state & helpers inside component (top area) ---
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const me = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();
  const displayName = me?.name || me?.username || "EESA";
  const initials = (displayName || "A")
    .split(" ").filter(Boolean).map(s => s[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    const onClick = (e) => { if (!userMenuRef.current?.contains(e.target)) setUserMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setUserMenuOpen(false); };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("click", onClick); window.removeEventListener("keydown", onKey); };
  }, []);

  // ======= DATA STATE (from OLD) =======
  const [allAppraisals, setAllAppraisals] = useState([]);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffView, setStaffView] = useState('menu'); // 'menu', 'add', 'list'
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffErr, setStaffErr] = useState("");
  const [msg, setMsg] = useState("");
  // Approval modal state
  const [editingAppraisal, setEditingAppraisal] = useState(null);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [adminMessage, setAdminMessage] = useState('');

  // Staff edit/reset modals
  const [editingStaff, setEditingStaff] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [manualPw, setManualPw] = useState("");

  // Staff create form
  const [form, setForm] = useState({ firstName: "", lastName: "", username: "", email: "", password: "" });

  // PDF preview overlay
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfFor, setPdfFor] = useState(null);


  // ======= TASKS STATE =======
  const [tasks, setTasks] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showAssignJobModal, setShowAssignJobModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    customerName: "",
    customerAddress: "",
    customerContact: "",
    description: "",
    assignedTo: "",
    priority: "Medium",
    deadline: "",
    jobTiming: "today" // 'today' or 'future'
  });

  // PDF Upload state
  const [previousReportPdf, setPreviousReportPdf] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  // Postcode lookup state
  const [postcode, setPostcode] = useState("");
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);

  // ======= ADMIN SETTINGS STATE =======
  const [adminSettings, setAdminSettings] = useState({
    username: "",
    password: "",
    email: ""
  });

  // Current admin details (fetched from API)
  const [currentAdmin, setCurrentAdmin] = useState({ username: "", email: "" });

  useEffect(() => {
    if (active === "settings") {
      api.me().then(user => {
        if (user) {
          setCurrentAdmin({
            username: user.username || "",
            email: user.email || ""
          });
        }
      }).catch(err => console.error("Failed to fetch admin details", err));
    }
  }, [active]);

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    if (!adminSettings.username && !adminSettings.password) {
      return setMsg("Please enter a new username or password to update.");
    }

    if (!window.confirm("Are you sure you want to update your admin credentials? You will need to login again.")) {
      return;
    }

    try {
      const payload = {};
      if (adminSettings.username) payload.newUsername = adminSettings.username;
      if (adminSettings.password) payload.newPassword = adminSettings.password;
      if (adminSettings.email) payload.newEmail = adminSettings.email;

      // Call API (we need to add this method to api service or call fetch directly)
      // Since api.js is imported, let's assume we can add it there or use a direct fetch for now if api.js isn't easily editable in this step.
      // But wait, I should check api.js first to see if I can add it there. 
      // For now, I'll use the `api` object if I can add the method, or just use `fetch` with the base URL.
      // Looking at imports: `import { api, API_BASE } from "../services/api";`

      const res = await fetch(`${API_BASE}/api/auth/admin/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // ✅ Important: Send session cookie
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed to update credentials");

      setMsg("✅ Credentials updated! Please login again.");
      setTimeout(async () => {
        if (typeof onLogout === 'function') {
          onLogout();
        } else {
          // Fallback if onLogout prop isn't passed
          try { await api.logout(); } catch { }
          nav("/");
        }
      }, 1500);

    } catch (e) {
      setMsg(e.message || "❌ Update failed");
    }
  };

  // ======= HELPERS (from OLD) =======


  // ======= DB CALLS (from OLD) =======
  const loadStaff = async () => {
    setStaffLoading(true);
    setStaffErr("");
    try {
      const list = await api.listStaff();
      setStaff(list || []);
    } catch (e) {
      setStaffErr(e.message || "Failed to load staff");
    } finally {
      setStaffLoading(false);
    }
  };

  const loadAppraisals = async () => {
    try {
      const appraisals = await api.getAllAppraisals();
      setAllAppraisals(appraisals);
      setRows(appraisals);
    } catch (e) {
      setMsg(e.message || "Failed to load appraisals");
    }
  };

  const deleteAppraisal = async (id) => {
    if (!window.confirm("Delete this appraisal? This cannot be undone.")) return;
    try {
      await api.deleteAppraisal(id);
      setMsg("✅ Appraisal deleted");
      setAllAppraisals((prev) => (prev || []).filter((a) => a._id !== id));
      setRows((prev) => (prev || []).filter((a) => a._id !== id));
    } catch (e) {
      setMsg(e?.message || "❌ Failed to delete appraisal");
    }
  };

  const removeStaff = async (id) => {
    if (!window.confirm("Delete this staff?")) return;
    try {
      await api.deleteStaff(id);
      setMsg("✅ Staff deleted");
      loadStaff();
    } catch (e) {
      setMsg(e.message || "❌ Failed to delete staff");
    }
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    setMsg("");
    const fn = (form.firstName || "").trim();
    const ln = (form.lastName || "").trim();
    if (!fn && !ln) return alert("Enter first name.");
    if (!fn && ln) return alert("Enter first name.");
    try {
      await api.createStaff({
        firstName: fn,
        lastName: ln || null,
        username: (form.username || "").trim(),
        email: (form.email || "").trim(),
        password: form.password,
      });
      setMsg("✅ Staff created");
      setForm({ firstName: "", lastName: "", username: "", email: "", password: "" });
      loadStaff();
    } catch (e) {
      setMsg(e.message || "❌ Failed to create staff");
    }
  };

  const handleApprovalSubmit = async () => {
    if (!editingAppraisal || !approvalStatus) {
      setMsg("Please select approval status");
      return;
    }

    try {
      await api.updateApprovalStatus(
        editingAppraisal._id,
        approvalStatus,
        adminMessage
      );

      setMsg(`✅ Appraisal ${approvalStatus}`);

      // Update local state
      setAllAppraisals(prev =>
        prev.map(a =>
          a._id === editingAppraisal._id
            ? { ...a, approvalStatus, adminMessage }
            : a
        )
      );

      // Close modal
      setApprovalModalOpen(false);
      setEditingAppraisal(null);
      setApprovalStatus('');
      setAdminMessage('');

    } catch (e) {
      setMsg(e.message || "❌ Failed to update approval");
    }
  };


  // ======= Folder Button Component =======
  const FolderButton = ({ regNumber, onClick }) => {
    const handleClick = async () => {
      try {
        const docs = await api.getUploadedDocuments();
        const regDocs = docs.filter(doc => doc.regNumber === regNumber);

        if (regDocs.length > 0) {
          onClick(regNumber);
        } else {
          alert(`No documents found for ${regNumber}`);
        }
      } catch (error) {
        console.error('Error checking documents:', error);
        alert('Unable to check documents');
      }
    };

    return (
      <button
        className="text-2xl hover:scale-110 transition-transform cursor-pointer"
        onClick={handleClick}
        title={`View documents for ${regNumber}`}
        type="button"
      >
        📁
      </button>
    );
  };

  // Folder Click Handler
  const handleFolderClick = (regNumber) => {
    setSelectedRegNumber(regNumber);
    setActive('documents');
  };

  // ======= PDF bits we re-use (from OLD) =======


  const generatePDF = async (data, { mode = 'download' } = {}) => {
    setPdfLoading(true);
    try {
      const url = await generatePDFUtil(data, { mode });
      if (mode === 'preview' && url) {
        setPdfPreviewUrl(url);
        // setPdfFor(data); // Already set by caller usually, but good to ensure? 
        // Caller sets pdfFor before calling generatePDF in some cases.
      }
    } catch (e) {
      console.error("PDF generation failed", e);
    } finally {
      setPdfLoading(false);
    }
  };

  // ======= EXPORT: CSV for all appraisals =======
  const handleExportCSV = () => {
    const rows = (allAppraisals || []).map(a => ({
      Reg: a.reg,
      Vehicle: `${a.make || ""} ${a.model || ""}`.trim() || a.vehicle || "",
      Date: a.date || (a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ""),
      ExteriorFaults: a?.faults?.exterior?.length || 0,
      InteriorFaults: a?.faults?.interior?.length || 0,
      TotalFaults:
        (a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0),
      SubmittedBy: a.submittedBy || "",
    }));

    const headers = Object.keys(
      rows[0] || {
        Reg: "", Vehicle: "", Date: "",
        ExteriorFaults: 0, InteriorFaults: 0, TotalFaults: 0, SubmittedBy: ""
      }
    );

    const csv = [
      headers.join(","),
      ...rows.map(r =>
        headers
          .map(h => String(r[h] ?? "").replace(/"/g, '""'))
          .map(v => /[",\n]/.test(v) ? `"${v}"` : v)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "appraisals_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ======= EXPORT: CSV for last 7 days (weekly) =======
  const handleExportWeeklyCSV = () => {
    // last 7 days (today inclusive)
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);

    const inLast7Days = (dt) => {
      const d = dt ? new Date(dt) : null;
      return d && d >= start && d <= end;
    };

    const weekly = (allAppraisals || []).filter(a =>
      inLast7Days(a.createdAt || a.date)
    );

    const rows = weekly.map(a => ({
      Reg: a.reg,
      Vehicle: `${a.make || ""} ${a.model || ""}`.trim() || a.vehicle || "",
      Date: a.date || (a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ""),
      ExteriorFaults: a?.faults?.exterior?.length || 0,
      InteriorFaults: a?.faults?.interior?.length || 0,
      TotalFaults: (a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0),
      SubmittedBy: a.submittedBy || "",
    }));

    const headers = Object.keys(
      rows[0] || {
        Reg: "", Vehicle: "", Date: "",
        ExteriorFaults: 0, InteriorFaults: 0, TotalFaults: 0, SubmittedBy: ""
      }
    );

    const csv = [
      headers.join(","),
      ...rows.map(r =>
        headers
          .map(h => String(r[h] ?? "").replace(/"/g, '""'))
          .map(v => /[",\n]/.test(v) ? `"${v}"` : v)
          .join(",")
      ),
    ].join("\n");

    const yyyy = end.getFullYear();
    const mm = String(end.getMonth() + 1).padStart(2, "0");
    const dd = String(end.getDate()).padStart(2, "0");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `appraisals_week_${yyyy}-${mm}-${dd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ======= EFFECTS =======
  useEffect(() => {
    loadStaff();
    loadAppraisals();
    loadTasks();
  }, []);

  // ======= TASKS LOGIC =======
  const loadTasks = async () => {
    setTaskLoading(true);
    try {
      const data = await api.getAllTasks();
      setTasks(data || []);
    } catch (e) {
      console.error("Failed to load tasks", e);
    } finally {
      setTaskLoading(false);
    }
  };

  // ======= NOTIFICATIONS =======
  const loadNotifications = async () => {
    try {
      setNotificationLoading(true);
      const data = await api.getNotifications();
      setNotifications(data || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.markNotificationAsRead(id);
      setNotifications(notifications.map(n =>
        n._id === id ? { ...n, read: true } : n
      ));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsAsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      // Mark as read if not already
      if (!notif.read) {
        await api.markNotificationAsRead(notif._id);
        setNotifications(notifications.map(n =>
          n._id === notif._id ? { ...n, read: true } : n
        ));
      }

      // Close notification panel
      setShowNotifications(false);

      // Navigate based on type
      if (notif.type === 'appraisal_submitted' && notif.appraisalId) {
        setActive('appraisals');
        const id = notif.appraisalId._id || notif.appraisalId;
        setHighlightedAppraisalId(id);
        setTimeout(() => setHighlightedAppraisalId(null), 5000);
      } else if (notif.type === 'job_completed' && notif.taskId) {
        setActive('tasks');
        const id = notif.taskId._id || notif.taskId;
        setHighlightedTaskId(id);
        setTimeout(() => setHighlightedTaskId(null), 5000);
      }
    } catch (err) {
      console.error('Failed to handle notification click:', err);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Load notifications on mount and auto-refresh
  // Load notifications on mount and auto-refresh
  useEffect(() => {
    let mounted = true;

    const fetchNotifs = async () => {
      if (!mounted) return;
      await loadNotifications();
    };

    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000); // Refresh every 30 seconds

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []); // Run once on mount

  // Postcode lookup handler
  const handlePostcodeLookup = async () => {
    const trimmedPostcode = postcode.trim().replace(/\s/g, ''); // Remove spaces for API call

    if (!trimmedPostcode) {
      setPostcodeError("Please enter a postcode");
      setShowSuggestions(false);
      return;
    }

    // Basic UK postcode validation
    const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
    if (!postcodeRegex.test(trimmedPostcode)) {
      setPostcodeError("Invalid UK postcode format");
      setShowSuggestions(false);
      return;
    }

    setPostcodeLoading(true);
    setPostcodeError("");
    setAddressSuggestions([]);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${API_BASE}/api/postcode/${encodeURIComponent(trimmedPostcode)}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // Debug: Log the actual API response structure
        console.log("Postcode API Response:", data);
        console.log("First address sample:", data.addresses?.[0]);

        if (data.addresses && data.addresses.length > 0) {
          setAddressSuggestions(data.addresses);
          setShowSuggestions(true);
          setSelectedAddressIndex(-1);
        } else {
          setPostcodeError("No addresses found for this postcode");
          setShowSuggestions(false);
        }
      } else if (response.status === 404) {
        setPostcodeError("Postcode not found. Please check and try again.");
        setShowSuggestions(false);
      } else if (response.status === 429) {
        setPostcodeError("Daily limit reached. Please try again tomorrow.");
        setShowSuggestions(false);
      } else {
        setPostcodeError("Failed to lookup postcode. Please try again.");
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Postcode lookup error:", error);
      setPostcodeError("Failed to lookup postcode. Please try again.");
      setShowSuggestions(false);
    } finally {
      setPostcodeLoading(false);
    }
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (address) => {
    setTaskForm({ ...taskForm, customerAddress: address });
    setShowSuggestions(false);
    setPostcode("");
    setAddressSuggestions([]);
    setMsg("✅ Address selected");
    setTimeout(() => setMsg(""), 3000);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e) => {
    if (!showSuggestions || addressSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedAddressIndex(prev =>
        prev < addressSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedAddressIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedAddressIndex >= 0) {
      e.preventDefault();
      handleAddressSelect(addressSuggestions[selectedAddressIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setMsg("");

    // Validate form
    if (!taskForm.title.trim() || !taskForm.assignedTo) {
      setMsg("Title and Staff Member are required");
      setTimeout(() => setMsg(""), 3000);
      return;
    }

    try {
      setPdfUploading(true);

      // If "today", set deadline to today's date automatically
      let finalDeadline = taskForm.deadline;
      if (taskForm.jobTiming === 'today') {
        finalDeadline = new Date().toISOString().split('T')[0];
      }

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('title', (taskForm.title || '').toUpperCase().replace(/\s+/g, ''));
      formData.append('customerName', taskForm.customerName || '');
      formData.append('customerAddress', taskForm.customerAddress || '');
      formData.append('customerContact', taskForm.customerContact || '');
      formData.append('description', taskForm.description || '');
      formData.append('assignedTo', taskForm.assignedTo);
      formData.append('priority', taskForm.priority || 'Medium');
      formData.append('deadline', finalDeadline || '');
      formData.append('status', 'Pending');
      formData.append('assignedBy', me.id);

      // Add PDF if selected
      if (previousReportPdf) {
        formData.append('previousReportPdf', previousReportPdf);
      }

      // Send request with FormData
      const response = await fetch(`${API_BASE}/api/tasks`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create task';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          } else {
            // Server returned non-JSON (likely HTML error page)
            const text = await response.text();
            console.error('Server returned non-JSON error:', text);
            errorMessage = `Server error (${response.status}). Please check backend logs.`;
          }
        } catch (parseError) {
          console.error('Error parsing server response:', parseError);
          errorMessage = `Server error (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      const res = await response.json();

      // Show success message with fault count if PDF was processed
      if (res.previousReport?.extractedFaults?.length > 0) {
        setMsg(`✅ Task created! ${res.previousReport.extractedFaults.length} faults extracted from PDF`);
      } else if (previousReportPdf) {
        setMsg("✅ Task created! PDF uploaded (no faults detected)");
      } else {
        setMsg("✅ Task created successfully");
      }

      setTasks([res, ...tasks]);
      setTaskForm({
        title: "",
        customerName: "",
        customerAddress: "",
        customerContact: "",
        description: "",
        assignedTo: "",
        priority: "Medium",
        deadline: "",
        jobTiming: "today"
      });
      setPreviousReportPdf(null);
      loadTasks();

      // Close the modal after successful creation
      setShowAssignJobModal(false);

      setTimeout(() => setMsg(""), 5000);
    } catch (e) {
      setMsg(e.message || "❌ Failed to create task");
      setTimeout(() => setMsg(""), 3000);
    } finally {
      setPdfUploading(false);
    }
  };

  // This useEffect handles loading data when the active tab changes
  useEffect(() => {
    if (active === "appraisals") {
      loadAppraisals();
    } else if (active === "staff") {
      loadStaff();
    } else if (active === "tasks") {
      loadTasks();
      loadStaff();
      loadAppraisals(); // Load appraisals to link with tasks
    } else if (active === "reports") {
      // Reports might need specific data loading
    }
  }, [active]);

  const generateTaskPDF = async (task) => {
    setPdfLoading(true);
    try {
      const url = await generateTaskPDFUtil(task);
      setPdfPreviewUrl(url);
      setPdfFor({ reg: task.title });
    } catch (e) {
      console.error("Task PDF generation failed", e);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleViewTask = (task) => {
    // If task is completed, try to find a matching appraisal
    if (task.status === 'Completed') {
      // Normalize reg/title
      const taskReg = (task.title || '').replace(/\s+/g, '').toUpperCase();

      // Find matching appraisal (latest first)
      const matchingAppraisal = (allAppraisals || [])
        .filter(a => (a.reg || '').replace(/\s+/g, '').toUpperCase() === taskReg)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      if (matchingAppraisal) {
        // Generate PDF for the appraisal
        generatePDF(matchingAppraisal, { mode: 'preview' });
        return;
      }
    }

    setViewTask(task);
  };

  const handleReviewTask = (task) => {
    setReviewTask(task);
    setReviewForm({
      status: task.approvalStatus === 'Pending' ? 'Approved' : task.approvalStatus,
      feedback: task.adminFeedback || ''
    });
  };

  const submitTaskReview = async (e) => {
    e.preventDefault();
    if (!reviewTask) return;

    try {
      await api.updateTask(reviewTask._id, {
        approvalStatus: reviewForm.status,
        adminFeedback: reviewForm.feedback
      });

      // Update local state
      setTasks(tasks.map(t =>
        t._id === reviewTask._id
          ? { ...t, approvalStatus: reviewForm.status, adminFeedback: reviewForm.feedback }
          : t
      ));

      setReviewTask(null);
      setMsg("✅ Job review updated");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error('Failed to review task:', err);
      setMsg("❌ Failed to update review");
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.deleteTask(id);
      setMsg("✅ Task deleted");
      setTimeout(() => setMsg(""), 3000);
      setTasks(prev => prev.filter(t => t._id !== id));
    } catch (e) {
      setMsg(e.message || "❌ Failed to delete task");
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask({
      ...task,
      assignedTo: task.assignedTo === 'TBC' ? 'TBC' : (task.assignedTo?._id || task.assignedTo),
      deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : ''
    });
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;

    try {
      const updateData = {
        title: editingTask.title,
        customerName: editingTask.customerName,
        customerAddress: editingTask.customerAddress,
        customerContact: editingTask.customerContact,
        description: editingTask.description,
        assignedTo: editingTask.assignedTo,
        priority: editingTask.priority,
        deadline: editingTask.deadline || null
      };

      await api.updateTask(editingTask._id, updateData);
      setMsg("✅ Task updated successfully");
      setTimeout(() => setMsg(""), 3000);

      // Reload tasks
      loadTasks();
      setEditingTask(null);
    } catch (e) {
      setMsg(e.message || "❌ Failed to update task");
      setTimeout(() => setMsg(""), 3000);
    }
  };



  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [sidebarOpen]);


  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ======= COMPUTED UI DATA =======
  const kpis = useMemo(() => {
    return [
      { label: "Total Appraisals", value: String(allAppraisals.length), icon: <FileText size={22} /> },
      { label: "Active Staff", value: String(staff.length), icon: <Users size={22} /> },
      {
        label: "Pending Reports",
        value: String(
          allAppraisals.filter(
            (a) => ((a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0)) > 0
          ).length
        ),
        icon: <BarChart2 size={22} />,
      },
      { label: "Settings Updated", value: "3", icon: <Settings size={22} /> }, // placeholder
    ];
  }, [allAppraisals, staff]);

  const activity = useMemo(() => {
    return (allAppraisals || [])
      .slice(0, 7)
      .map((a, i) => ({
        id: a._id || i,
        title: `Appraisal ${a.reg || a.vehicle || a._id}`,
        time: a.createdAt ? new Date(a.createdAt).toLocaleString() : "recent",
      }));
  }, [allAppraisals]);

  // Build weekly chart from createdAt (fallback to mock when none)
  const chartData = useMemo(() => {
    if (!allAppraisals?.length) {
      return [
        { name: "Mon", appraisals: 12, faults: 4 },
        { name: "Tue", appraisals: 18, faults: 6 },
        { name: "Wed", appraisals: 15, faults: 5 },
        { name: "Thu", appraisals: 22, faults: 8 },
        { name: "Fri", appraisals: 19, faults: 7 },
        { name: "Sat", appraisals: 24, faults: 9 },
        { name: "Sun", appraisals: 16, faults: 5 },
      ];
    }
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const map = new Map(days.map((d) => [d, { appraisals: 0, faults: 0 }]));
    for (const a of allAppraisals) {
      const d = a.createdAt ? new Date(a.createdAt) : new Date();
      const key = days[d.getDay()];
      const faults = (a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0);
      const ent = map.get(key) || { appraisals: 0, faults: 0 };
      ent.appraisals += 1;
      ent.faults += faults ? 1 : 0;
      map.set(key, ent);
    }
    return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name) => ({
      name,
      appraisals: map.get(name)?.appraisals || 0,
      faults: map.get(name)?.faults || 0,
    }));
  }, [allAppraisals]);

  // ======= REPORTS STATS =======
  const pendingCount = useMemo(() => {
    return (allAppraisals || []).filter(a =>
      ((a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0)) > 0
    ).length;
  }, [allAppraisals]);

  const totalFaultItems = useMemo(() => {
    return (allAppraisals || []).reduce((sum, a) =>
      sum + (a?.faults?.exterior?.length || 0) + (a?.faults?.interior?.length || 0), 0);
  }, [allAppraisals]);

  const avgFaultsPerAppraisal = useMemo(() => {
    const total = allAppraisals?.length || 0;
    return total ? (totalFaultItems / total).toFixed(2) : "0.00";
  }, [allAppraisals, totalFaultItems]);

  const cleanCount = useMemo(() => {
    const total = allAppraisals?.length || 0;
    return Math.max(0, total - pendingCount);
  }, [allAppraisals, pendingCount]);

  const faultsPieData = useMemo(() => ([
    { name: "With Faults", value: pendingCount },
    { name: "Clean", value: cleanCount },
  ]), [pendingCount, cleanCount]);


  // ======= LOGOUT =======
  const handleLogout = async () => {
    const ok = typeof window !== "undefined" ? window.confirm("Sign out now?") : true;
    if (!ok) return;
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      try {
        await api.logout();
      } catch { }
      nav("/");
    }
  };

  // ======= UI SUB-COMPONENT =======
  const NavBtn = ({ icon, label, isActive = false, onClick }) => (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      className={`flex items-center gap-3 w-full text-left px-4 py-2 rounded-md transition ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50 hover:text-blue-700"
        }`}
    >
      {icon}
      <span className={`inline ${desktopCollapsed ? "lg:hidden" : "lg:inline"}`}>{label}</span>

    </button>
  );

  return (
    <div className="h-[100dvh] bg-gray-50 flex overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r shadow-sm
    transform transition-transform duration-200
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
    flex flex-col justify-between overflow-y-auto
    w-72 ${desktopCollapsed ? "lg:w-20" : "lg:w-72"}`}
      >


        <div>
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <LayoutDashboard className="text-blue-700" size={20} />
              <h2 className={`text-lg font-bold text-gray-800 block ${desktopCollapsed ? "lg:hidden" : "lg:block"}`}>
                Admin Panel
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="hidden lg:inline-flex p-2 rounded-md hover:bg-gray-100"
                onClick={() => setDesktopCollapsed((v) => !v)}
                title={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {desktopCollapsed ? (
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <button className="lg:hidden p-2 rounded-md hover:bg-gray-100" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">
                <X className="text-gray-600" size={20} />
              </button>
            </div>
          </div>

          <nav className="p-3 space-y-2">
            <NavBtn icon={<LayoutDashboard size={18} />} label="Dashboard" isActive={active === "dashboard"} onClick={() => goTo("dashboard")} />
            <NavBtn icon={<FileText size={18} />} label="Appraisals" isActive={active === "appraisals"} onClick={() => goTo("appraisals")} />
            <NavBtn icon={<Users size={18} />} label="Staff" isActive={active === "staff"} onClick={() => goTo("staff")} />
            <NavBtn icon={<ClipboardList size={18} />} label="Jobs" isActive={active === "tasks"} onClick={() => goTo("tasks")} />
            <NavBtn icon={<BarChart2 size={18} />} label="Reports" isActive={active === "reports"} onClick={() => goTo("reports")} />
            <NavBtn
              icon={<FileText size={18} />}
              label="Documents"
              isActive={active === "documents"}
              onClick={() => goTo("documents")}
            />
            <NavBtn icon={<Settings size={18} />} label="Settings" isActive={active === "settings"} onClick={() => goTo("settings")} />

          </nav>
        </div>

        <div className="p-4 border-t">
          <button onClick={handleLogout} className="flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 w-full justify-start">
            <LogOut size={18} />
            <span className={`inline ${desktopCollapsed ? "lg:hidden" : "lg:inline"}`}>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 p-4 lg:p-8 overflow-y-auto h-[100dvh] [-webkit-overflow-scrolling:touch] overscroll-y-contain ${desktopCollapsed ? "lg:ml-20" : "lg:ml-72"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8 lg:mb-10">
          <div className="flex items-center gap-3">
            <button className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border bg-white hover:bg-gray-50" onClick={() => setSidebarOpen(true)} aria-label="Open sidebar">
              <Menu className="text-gray-700" size={20} />
            </button>
            {active === "dashboard" ? (
              <img
                src={businessLogo}
                alt="Heston Workspace"
                className="h-8 lg:h-10 w-auto object-contain"
              />
            ) : (
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 capitalize">
                {active === 'tasks' ? 'Jobs' : active}
              </h1>
            )}
          </div>

          {/* --- HEADER RIGHT: Notifications + User Menu --- */}
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition"
              >
                <Bell size={20} className="text-gray-700" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                      <div>
                        <h3 className="font-bold text-gray-900">Notifications</h3>
                        <p className="text-xs text-gray-500">{unreadCount} unread</p>
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    {notificationLoading ? (
                      <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No notifications</div>
                    ) : (
                      <div className="divide-y">
                        {notifications.map((notif) => (
                          <div
                            key={notif._id}
                            className={`p-4 hover:bg-gray-50 transition cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="text-sm text-gray-900">{notif.message}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(notif.createdAt).toLocaleString()}
                                </p>
                              </div>
                              {!notif.read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent opening when just marking as read
                                    handleMarkAsRead(notif._id);
                                  }}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded text-xs"
                                  title="Mark as read"
                                >
                                  ✓
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-2 bg-white border rounded-full pl-1 pr-3 py-1.5 shadow-sm hover:bg-gray-50"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <div className="w-8 h-8 rounded-full bg-blue-600/10 text-blue-700 font-semibold grid place-items-center">
                  {initials}
                </div>
                <span className="hidden sm:inline text-gray-800 font-medium">{displayName}</span>
                <ChevronDown size={16} className="text-gray-500" />
              </button>

              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50"
                >
                  <button
                    onClick={() => { setUserMenuOpen(false); goTo("settings"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <SettingsIcon size={16} className="text-gray-600" />
                    <span>Settings</span>
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-red-600"
                  >
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {active === "dashboard" && (
          <>
            {/* Chart Card (TOP) */}
            <div className="bg-white rounded-xl shadow-sm border p-4 lg:p-6 mb-8 lg:mb-10 [touch-action:pan-y]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Weekly Activity</h2>
                <div className="text-sm text-gray-500">Appraisals vs Faults</div>
              </div>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="appraisals" stroke="#2563eb" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="faults" stroke="#059669" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Modules (BOTTOM) */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Go To</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {[
                {
                  title: "Appraisals",
                  desc: "Manage and view all appraisals",
                  total: `Total: ${allAppraisals.length}`,
                  icon: <FileText size={28} />,
                  color: "bg-blue-100 text-blue-800",
                  onClick: () => goTo("appraisals"),
                },
                {
                  title: "Staff",
                  desc: "Create & manage staff",
                  total: `Total: ${staff.length}`,
                  icon: <Users size={28} />,
                  color: "bg-purple-100 text-purple-800",
                  onClick: () => goTo("staff"),
                },
                {
                  title: "Reports",
                  desc: "Analytics & exports",

                  icon: <BarChart2 size={28} />,
                  color: "bg-gray-100 text-gray-800",
                  onClick: () => goTo("reports"),
                },
                {
                  title: "Jobs",
                  desc: "Assign & manage jobs",
                  total: `Total: ${tasks.length}`,
                  icon: <ClipboardList size={28} />,
                  color: "bg-orange-100 text-orange-800",
                  onClick: () => goTo("tasks"),
                },
                {
                  title: "Weekly Appraisals Report",
                  desc: "Download this week's CSV",
                  icon: <FileText size={28} />,
                  color: "bg-green-100 text-green-800",
                },


              ].map((m, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition flex flex-col justify-between cursor-pointer"
                  onClick={m.onClick}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-lg mb-4 ${m.color}`}>
                    {m.icon}
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-gray-600">{m.desc}</p>
                      {m.total && (
                        <p className="text-sm font-medium text-gray-800 mt-2">{m.total}</p>
                      )}
                    </div>

                    {m.title === "Weekly Appraisals Report" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleExportWeeklyCSV(); }}
                        className="px-3 py-2 rounded-lg bg-blue-600 text-white"
                        title="Download weekly CSV"
                      >
                        Export CSV
                      </button>
                    )}
                  </div>

                </motion.div>
              ))}
            </div>
          </>
        )}


        {/* APPRAISALS */}
        {active === "appraisals" && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">All Appraisals</h2>
            </div>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-[10px] sm:text-sm">
                    <th className="text-left py-2 px-1 sm:px-2">Reg</th>
                    <th className="text-left py-2 px-1 sm:px-2">Date</th>
                    <th className="text-left py-2 px-1 sm:px-2">Grade</th>
                    <th className="text-left py-2 px-1 sm:px-2">By</th>
                    <th className="text-left py-2 px-1 sm:px-2">Status</th>
                    <th className="text-center py-2 px-1 sm:px-2">Folder</th>
                    <th className="text-center py-2 px-1 sm:px-2">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {allAppraisals.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-gray-500">
                        No appraisals found.
                      </td>
                    </tr>
                  ) : (
                    allAppraisals.map((a) => (
                      <tr
                        key={a._id}
                        className={`border-t transition-colors ${a._id === highlightedAppraisalId ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : 'hover:bg-gray-50'}`}
                      >
                        <td className="py-2 px-1 sm:px-2 text-[10px] sm:text-sm">{a.reg}</td>
                        <td className="py-2 px-1 sm:px-2 text-[10px] sm:text-sm whitespace-nowrap">{a.date || (a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—")}</td>
                        <td className="py-2 px-1 sm:px-2">
                          {a.overallGrade ? (
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white font-bold text-[8px] sm:text-xs ${a.overallGrade === '1' || a.overallGrade === 1
                                ? 'bg-[#4CAF50]'
                                : a.overallGrade === '2' || a.overallGrade === 2
                                  ? 'bg-[#8BC34A]'
                                  : a.overallGrade === '3' || a.overallGrade === 3
                                    ? 'bg-[#FFC107]'
                                    : a.overallGrade === '4' || a.overallGrade === 4
                                      ? 'bg-[#FF9800]'
                                      : a.overallGrade === '5' || a.overallGrade === 5
                                        ? 'bg-[#B71C1C]'
                                        : String(a.overallGrade).toUpperCase() === 'U'
                                          ? 'bg-[#9E9E9E]'
                                          : 'bg-gray-400'
                                }`}
                            >
                              {a.overallGrade}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-2 px-1 sm:px-2 text-[10px] sm:text-sm">{a.submittedBy || "—"}</td>
                        <td className="py-2 px-1 sm:px-2">
                          <div className="flex flex-col items-start gap-1">
                            <span
                              className={`px-1 sm:px-2 py-0.5 rounded-full text-[8px] sm:text-xs font-semibold whitespace-nowrap ${a.approvalStatus === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : a.approvalStatus === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                                }`}
                            >
                              <span className="hidden sm:inline">
                                {a.approvalStatus === 'approved'
                                  ? '✓ Approved'
                                  : a.approvalStatus === 'rejected'
                                    ? '✗ Rejected'
                                    : '⏳ Pending'}
                              </span>
                              <span className="sm:hidden">
                                {a.approvalStatus === 'approved' ? '✓' : a.approvalStatus === 'rejected' ? '✗' : '⏳'}
                              </span>
                            </span>
                            {a.adminMessage && (
                              <span className="text-[8px] sm:text-[10px] text-gray-500 italic hidden sm:flex items-center gap-1">
                                💬 {a.adminMessage}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-1 sm:px-2 text-center">
                          <FolderButton
                            regNumber={a.reg}
                            onClick={handleFolderClick}
                          />
                        </td>
                        <td className="py-2 px-1 sm:px-2">
                          <div className="flex gap-1 items-center justify-center">
                            <button
                              className="p-1.5 sm:px-2 sm:py-1 rounded-lg bg-blue-600 text-white text-xs"
                              onClick={() => {
                                setPdfFor(a);
                                generatePDF(a, { mode: "preview" });
                              }}
                              title="View"
                            >
                              <Eye size={16} className="sm:hidden" />
                              <span className="hidden sm:inline">View</span>
                            </button>
                            <button
                              className="p-1.5 sm:px-2 sm:py-1 rounded-lg bg-green-50 text-green-600 text-xs"
                              onClick={() => {
                                setEditingAppraisal(a);
                                setApprovalStatus(a.approvalStatus || '');
                                setAdminMessage(a.adminMessage || '');
                                setApprovalModalOpen(true);
                              }}
                              title="Review"
                            >
                              <CheckCircle size={16} className="sm:hidden" />
                              <span className="hidden sm:inline">Review</span>
                            </button>
                            <button
                              className="p-1.5 sm:px-2 sm:py-1 rounded-lg bg-red-50 text-red-600 text-xs"
                              onClick={() => deleteAppraisal(a._id)}
                              title="Delete"
                            >
                              <Trash2 size={16} className="sm:hidden" />
                              <span className="hidden sm:inline">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* STAFF (Unified: Create + Manage) */}
        {active === "staff" && (
          <>
            {/* Menu View */}
            {staffView === 'menu' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-10">
                {/* Card 1: Add New Staff */}
                <div
                  className="bg-white p-8 rounded-xl shadow-md border hover:shadow-lg transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-4 group"
                  onClick={() => setStaffView('add')}
                >
                  <div className="p-4 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors">
                    <UserPlus size={48} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Add New Staff</h3>
                    <p className="text-gray-500 mt-2">Create accounts for new team members</p>
                  </div>
                </div>

                {/* Card 2: Registered Staff */}
                <div
                  className="bg-white p-8 rounded-xl shadow-md border hover:shadow-lg transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-4 group"
                  onClick={() => setStaffView('list')}
                >
                  <div className="p-4 bg-green-50 rounded-full group-hover:bg-green-100 transition-colors">
                    <Users size={48} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Registered Staff</h3>
                    <p className="text-gray-500 mt-2">View and manage existing accounts</p>
                  </div>
                </div>
              </div>
            )}

            {/* Add Staff View */}
            {staffView === 'add' && (
              <div className="max-w-4xl mx-auto">
                <button
                  onClick={() => setStaffView('menu')}
                  className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
                >
                  <ArrowLeft size={20} /> Back to Menu
                </button>

                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                  <h2 className="text-lg font-semibold mb-4">Add New Staff</h2>
                  <form onSubmit={submitStaff} className="grid sm:grid-cols-2 gap-4">
                    <input className="border rounded-lg px-3 py-2" placeholder="First name"
                      value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
                    <input className="border rounded-lg px-3 py-2" placeholder="Last name (optional)"
                      value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                    <input className="border rounded-lg px-3 py-2" placeholder="Username"
                      value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
                    <input className="border rounded-lg px-3 py-2" placeholder="Email" type="email"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    <input className="border rounded-lg px-3 py-2 sm:col-span-2" placeholder="Password" type="password"
                      value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                    <div className="sm:col-span-2 flex justify-end">
                      <button className="px-4 py-2 rounded-lg bg-blue-600 text-white">Create</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Registered Staff View */}
            {staffView === 'list' && (
              <div className="max-w-6xl mx-auto">
                <button
                  onClick={() => setStaffView('menu')}
                  className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
                >
                  <ArrowLeft size={20} /> Back to Menu
                </button>

                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Registered Staff</h2>
                    <button className="px-3 py-2 rounded-lg border" onClick={loadStaff}>Refresh</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed sm:table-auto">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600">
                          <th className="text-left py-2 px-2 sm:px-3">Name</th>
                          <th className="text-left py-2 px-2 sm:px-3">Username</th>
                          <th className="text-left py-2 px-2 sm:px-3 hidden sm:table-cell">Email</th>
                          <th className="text-center py-2 px-2 sm:px-3">OTP Login</th>
                          <th className="text-right py-2 px-2 sm:px-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffLoading ? (
                          <tr><td colSpan={5} className="py-6 text-center text-gray-500">Loading…</td></tr>
                        ) : staffErr ? (
                          <tr><td colSpan={5} className="py-6 text-center text-red-600">{staffErr}</td></tr>
                        ) : staff.length === 0 ? (
                          <tr><td colSpan={5} className="py-6 text-center text-gray-500">No staff yet.</td></tr>
                        ) : (
                          staff.map((s) => (
                            <tr key={s._id} className="border-t">
                              <td className="py-2 px-2 sm:px-3">{[s.firstName, s.lastName].filter(Boolean).join(" ")}</td>
                              <td className="py-2 px-2 sm:px-3">{s.username}</td>
                              <td className="py-2 px-2 sm:px-3 hidden sm:table-cell">{s.email}</td>
                              <td className="py-2 px-2 sm:px-3 text-center">
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Are you sure you want to ${s.isTwoFactorEnabled ? 'DISABLE' : 'ENABLE'} OTP Login for ${s.firstName}?`)) return;
                                    try {
                                      await api.toggleStaff2FA(s._id, !s.isTwoFactorEnabled);
                                      loadStaff(); // Refresh list
                                      setMsg(`OTP Login ${!s.isTwoFactorEnabled ? 'Enabled' : 'Disabled'} for ${s.firstName}`);
                                    } catch (e) {
                                      setMsg(e.message || 'Failed to toggle OTP Login');
                                    }
                                  }}
                                  className={`px-2 py-1 rounded text-xs font-bold ${s.isTwoFactorEnabled
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-gray-100 text-gray-500 border border-gray-300'
                                    }`}
                                >
                                  {s.isTwoFactorEnabled ? 'On' : 'Off'}
                                </button>
                              </td>
                              <td className="py-2 px-2 sm:px-3 text-right">
                                <div className="inline-flex gap-1 sm:gap-2">
                                  <button
                                    className="p-1.5 sm:px-3 sm:py-1 rounded-lg border"
                                    onClick={() => { setEditingStaff(s); setEditOpen(true); }}
                                    title="Edit"
                                  >
                                    <Edit2 size={16} className="sm:hidden" />
                                    <span className="hidden sm:inline">Edit</span>
                                  </button>
                                  <button
                                    className="p-1.5 sm:px-3 sm:py-1 rounded-lg border"
                                    onClick={() => { setEditingStaff(s); setTempPassword(""); setManualPw(""); setResetOpen(true); }}
                                    title="Reset Password"
                                  >
                                    <RotateCcw size={16} className="sm:hidden" />
                                    <span className="hidden sm:inline">Reset</span>
                                  </button>
                                  <button
                                    className="p-1.5 sm:px-3 sm:py-1 rounded-lg bg-red-50 text-red-600"
                                    onClick={() => removeStaff(s._id)}
                                    title="Delete"
                                  >
                                    <Trash2 size={16} className="sm:hidden" />
                                    <span className="hidden sm:inline">Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          </>
        )}

        {/* TASKS */}
        {active === "tasks" && (
          <div className="space-y-6">
            {/* Page Header with Stats */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
              {/* Title and Stats Row - Side by side on desktop */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Job Management</h1>
                  <p className="text-blue-100">Assign and track jobs for your team</p>
                </div>

                {/* Stats - Grid on mobile, Flex on desktop */}
                <div className="grid grid-cols-3 md:flex md:items-center gap-3 md:gap-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 md:px-4 py-3 text-center">
                    <div className="text-xl md:text-2xl font-bold">{tasks.length}</div>
                    <div className="text-xs text-blue-100">Total Jobs</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 md:px-4 py-3 text-center">
                    <div className="text-xl md:text-2xl font-bold">{tasks.filter(t => t.status === 'Pending').length}</div>
                    <div className="text-xs text-blue-100">Pending</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 md:px-4 py-3 text-center">
                    <div className="text-xl md:text-2xl font-bold">{tasks.filter(t => t.status === 'Completed').length}</div>
                    <div className="text-xs text-blue-100">Completed</div>
                  </div>
                </div>
              </div>

              {/* Assign Job Button - Full Width on Mobile, Centered on Desktop */}
              <div className="flex justify-center pt-2 border-t border-white/20">
                <button
                  onClick={() => setShowAssignJobModal(true)}
                  className="w-full md:w-auto bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors shadow-lg flex items-center justify-center gap-2 mt-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Assign Job
                </button>
              </div>
            </div>




            {/* Assign Job Modal */}
            {showAssignJobModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                      <ClipboardList size={28} />
                      Assign New Job
                    </h2>
                    <button
                      onClick={() => setShowAssignJobModal(false)}
                      className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleCreateTask}>
                      {/* Row 1: Reg Number, Customer Name, Contact, Job Timing */}
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">
                            Reg Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all uppercase font-bold"
                            placeholder="E.g., WR12 3AB"
                            value={taskForm.title}
                            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value.toUpperCase() })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">Customer Name</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="E.g., John Smith"
                            value={taskForm.customerName}
                            onChange={(e) => setTaskForm({ ...taskForm, customerName: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">Contact Number</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="E.g., 07700 900123"
                            value={taskForm.customerContact}
                            onChange={(e) => setTaskForm({ ...taskForm, customerContact: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">Job For</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setTaskForm({ ...taskForm, jobTiming: 'today' })}
                              className={`flex-1 py-2 rounded-lg font-semibold transition-all ${taskForm.jobTiming === 'today' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                              Today
                            </button>
                            <button
                              type="button"
                              onClick={() => setTaskForm({ ...taskForm, jobTiming: 'future' })}
                              className={`flex-1 py-2 rounded-lg font-semibold transition-all ${taskForm.jobTiming === 'future' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                              Future
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Address with Postcode Lookup */}
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-bold text-gray-800 mb-1">🔍 Postcode Lookup</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all uppercase"
                              placeholder="SW1A 1AA"
                              value={postcode}
                              onChange={(e) => { setPostcode(e.target.value); setPostcodeError(""); }}
                              onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePostcodeLookup(); } }}
                              onKeyDown={handleKeyDown}
                            />
                            <button
                              type="button"
                              onClick={handlePostcodeLookup}
                              disabled={postcodeLoading}
                              className={`px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap ${postcodeLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                            >
                              {postcodeLoading ? '...' : 'Find'}
                            </button>
                          </div>
                          {postcodeError && <p className="text-red-500 text-xs mt-1">{postcodeError}</p>}
                          {showSuggestions && addressSuggestions.length > 0 && (
                            <div className="mt-2 border border-gray-300 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg z-50">
                              {addressSuggestions.map((addr, index) => {
                                // Handle both string format and object format from API
                                const isString = typeof addr === 'string';
                                const line1 = isString ? addr.split(',')[0]?.trim() : (addr.line_1 || addr.Line1 || addr.address_line_1 || addr.formatted_address?.[0] || '');
                                const line2 = isString ? addr.split(',')[1]?.trim() : (addr.line_2 || addr.Line2 || addr.address_line_2 || addr.formatted_address?.[1] || '');
                                const town = isString ? addr.split(',').slice(-2, -1)[0]?.trim() : (addr.town_or_city || addr.TownOrCity || addr.post_town || addr.city || '');
                                const postCode = isString ? addr.split(',').slice(-1)[0]?.trim() : (addr.postcode || addr.Postcode || postcode.toUpperCase() || '');
                                const fullAddress = isString ? addr : [line1, line2, town, postCode].filter(Boolean).join(', ');

                                return (
                                  <div
                                    key={index}
                                    onClick={() => handleAddressSelect(fullAddress)}
                                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${index === selectedAddressIndex ? 'bg-blue-100' : ''}`}
                                  >
                                    <div className="text-sm font-medium text-gray-900">{line1 || 'Address ' + (index + 1)}</div>
                                    {line2 && <div className="text-xs text-gray-600">{line2}</div>}
                                    <div className="text-xs text-gray-500">{[town, postCode].filter(Boolean).join(', ')}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-bold text-gray-800 mb-1">Customer Address</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Address will appear here after lookup, or enter manually"
                            value={taskForm.customerAddress}
                            onChange={(e) => setTaskForm({ ...taskForm, customerAddress: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* Row 3: Description and Assign To */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">Description</label>
                          <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                            rows={3}
                            placeholder="Add task details or notes..."
                            value={taskForm.description}
                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-1">
                            Assign To <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <select
                              required={taskForm.assignedTo !== 'TBC'}
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all"
                              value={taskForm.assignedTo}
                              onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                              disabled={taskForm.assignedTo === 'TBC'}
                            >
                              <option value="">Select Staff...</option>
                              {staff.map(s => (
                                <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setTaskForm({ ...taskForm, assignedTo: taskForm.assignedTo === 'TBC' ? '' : 'TBC' })}
                              className={`px-4 py-2 rounded-lg font-bold transition-colors ${taskForm.assignedTo === 'TBC' ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                            >
                              TBC
                            </button>
                          </div>
                          {taskForm.assignedTo === 'TBC' && (
                            <p className="text-orange-600 text-xs mt-1 font-semibold">⚠️ Staff dropdown disabled - TBC selected</p>
                          )}
                        </div>
                      </div>

                      {/* Deadline (only if Future is selected and not TBC) */}
                      {taskForm.jobTiming === 'future' && taskForm.assignedTo !== 'TBC' && (
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-800 mb-1">Deadline</label>
                          <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={taskForm.deadline}
                            onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
                          />
                        </div>
                      )}

                      {/* PDF Upload */}
                      <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-800 mb-1">
                          Upload Previous Report <span className="text-gray-500 text-xs">(Optional PDF)</span>
                        </label>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setPreviousReportPdf(e.target.files[0])}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-500 mt-1">AI will extract faults from PDF and mark them on vehicle diagrams</p>
                      </div>

                      {/* Submit Button */}
                      <div className="flex gap-3 pt-4 border-t">
                        <button
                          type="submit"
                          disabled={pdfUploading}
                          className={`flex-1 py-3 px-6 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 ${pdfUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                        >
                          {pdfUploading ? 'Processing...' : <><ClipboardList size={18} /> Assign Job</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAssignJobModal(false)}
                          className="px-6 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            {/* Task List - Split into Today and Future */}
            <div className="space-y-8">
              {/* Assigned Jobs */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    Assigned Jobs
                  </h2>
                  <button className="px-3 py-2 rounded-lg border hover:bg-gray-50" onClick={loadTasks}>Refresh</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-900 font-bold border-b-2">
                        <th className="text-left py-3 px-4 w-1/12">Reg Num</th>
                        <th className="text-left py-3 px-4 w-1/8">Customer Name</th>
                        <th className="text-left py-3 px-4 w-1/6">Address</th>
                        <th className="text-left py-3 px-4 w-1/12">Contact</th>
                        <th className="text-left py-3 px-4 w-1/8">Assigned To</th>
                        <th className="text-center py-3 px-4 w-24">Status</th>
                        <th className="text-center py-3 px-4 w-24">Approval</th>
                        <th className="text-center py-3 px-4 w-24">Deadline</th>
                        <th className="text-center py-3 px-4 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskLoading ? (
                        <tr><td colSpan={9} className="py-8 text-center text-gray-500">Loading…</td></tr>
                      ) : tasks.filter(t => t.status !== 'Completed').length === 0 ? (
                        <tr><td colSpan={9} className="py-8 text-center text-gray-500">No assigned jobs.</td></tr>
                      ) : (
                        tasks.filter(t => t.status !== 'Completed').map((t) => (
                          <tr key={t._id} className={`border-b transition-colors ${t._id === highlightedTaskId ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : 'hover:bg-gray-50'}`}>
                            <td className="py-3 px-4">
                              <div className="font-bold text-gray-900">{t.title}</div>
                              {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-700">{t.customerName || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-600 text-xs max-w-[280px]" title={t.customerAddress}>{t.customerAddress || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-600 text-xs">{t.customerContact || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              {t.assignedTo === 'TBC' ? (
                                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 border border-orange-300">
                                  TBC
                                </span>
                              ) : t.assignedTo ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                    {t.assignedTo.firstName?.[0]}{t.assignedTo.lastName?.[0]}
                                  </div>
                                  <span className="text-gray-700">{t.assignedTo.firstName} {t.assignedTo.lastName}</span>
                                </div>
                              ) : <span className="text-gray-400 italic">Unassigned</span>}
                            </td>

                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${t.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                t.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${t.approvalStatus === 'Approved' ? 'bg-green-100 text-green-800' :
                                t.approvalStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                {t.approvalStatus || 'Pending'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600">
                              {t.assignedTo === 'TBC' ? '—' : (t.deadline ? new Date(t.deadline).toLocaleDateString() : "—")}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-1 sm:gap-2">
                                <button
                                  onClick={() => handleViewTask(t)}
                                  className="p-1.5 sm:px-3 sm:py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                                  title="View"
                                >
                                  <Eye size={16} className="sm:hidden" />
                                  <span className="hidden sm:inline">View</span>
                                </button>
                                <button
                                  onClick={() => handleEditTask(t)}
                                  className="p-1.5 sm:px-3 sm:py-1 bg-amber-50 text-amber-700 rounded-lg text-xs hover:bg-amber-100 border border-amber-200"
                                  title="Edit Task"
                                >
                                  <Edit2 size={16} className="sm:hidden" />
                                  <span className="hidden sm:inline">Edit</span>
                                </button>
                                {t.status === 'Completed' && (
                                  <button
                                    onClick={() => handleReviewTask(t)}
                                    className="p-1.5 sm:px-3 sm:py-1 bg-green-50 text-green-600 rounded-lg text-xs hover:bg-green-100"
                                    title="Review"
                                  >
                                    <CheckCircle size={16} className="sm:hidden" />
                                    <span className="hidden sm:inline">Review</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteTask(t._id)}
                                  className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete Task"
                                >
                                  <Trash2 size={16} className="sm:hidden" />
                                  <Trash2 size={18} className="hidden sm:block" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Completed Jobs */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Completed Jobs
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-900 font-bold border-b-2">
                        <th className="text-left py-3 px-4 w-1/12">Reg Num</th>
                        <th className="text-left py-3 px-4 w-1/8">Customer Name</th>
                        <th className="text-left py-3 px-4 w-1/6">Address</th>
                        <th className="text-left py-3 px-4 w-1/12">Contact</th>
                        <th className="text-left py-3 px-4 w-1/8">Assigned To</th>
                        <th className="text-center py-3 px-4 w-24">Status</th>
                        <th className="text-center py-3 px-4 w-24">Approval</th>
                        <th className="text-center py-3 px-4 w-24">Deadline</th>
                        <th className="text-center py-3 px-4 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskLoading ? (
                        <tr><td colSpan={9} className="py-8 text-center text-gray-500">Loading…</td></tr>
                      ) : tasks.filter(t => t.status === 'Completed').length === 0 ? (
                        <tr><td colSpan={9} className="py-8 text-center text-gray-500">No completed jobs yet.</td></tr>
                      ) : (
                        tasks.filter(t => t.status === 'Completed').map((t) => (
                          <tr key={t._id} className={`border-b transition-colors ${t._id === highlightedTaskId ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : 'hover:bg-gray-50'}`}>
                            <td className="py-3 px-4">
                              <div className="font-bold text-gray-900">{t.title}</div>
                              {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-700">{t.customerName || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-600 text-xs max-w-[280px]" title={t.customerAddress}>{t.customerAddress || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-gray-600 text-xs">{t.customerContact || '—'}</div>
                            </td>
                            <td className="py-3 px-4">
                              {t.assignedTo === 'TBC' ? (
                                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 border border-orange-300">
                                  TBC
                                </span>
                              ) : t.assignedTo ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                    {t.assignedTo.firstName?.[0]}{t.assignedTo.lastName?.[0]}
                                  </div>
                                  <span className="text-gray-700">{t.assignedTo.firstName} {t.assignedTo.lastName}</span>
                                </div>
                              ) : <span className="text-gray-400 italic">Unassigned</span>}
                            </td>

                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${t.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                t.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${t.approvalStatus === 'Approved' ? 'bg-green-100 text-green-800' :
                                t.approvalStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                {t.approvalStatus || 'Pending'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600">
                              {t.assignedTo === 'TBC' ? '—' : (t.deadline ? new Date(t.deadline).toLocaleDateString() : "—")}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-1 sm:gap-2">
                                <button
                                  onClick={() => handleViewTask(t)}
                                  className="p-1.5 sm:px-3 sm:py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                                  title="View"
                                >
                                  <Eye size={16} className="sm:hidden" />
                                  <span className="hidden sm:inline">View</span>
                                </button>
                                {t.status === 'Completed' && (
                                  <button
                                    onClick={() => handleReviewTask(t)}
                                    className="p-1.5 sm:px-3 sm:py-1 bg-green-50 text-green-600 rounded-lg text-xs hover:bg-green-100"
                                    title="Review"
                                  >
                                    <CheckCircle size={16} className="sm:hidden" />
                                    <span className="hidden sm:inline">Review</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteTask(t._id)}
                                  className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete Task"
                                >
                                  <Trash2 size={16} className="sm:hidden" />
                                  <Trash2 size={18} className="hidden sm:block" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div >
        )
        }

        {/* SETTINGS */}
        {
          active === "settings" && (
            <div className="bg-white p-6 rounded shadow border border-gray-100">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-400" />
                Security
              </h2>

              <TwoFactorSetup user={me} onUpdate={(val) => {
                // Update local storage user object if 2FA status changes
                const updatedUser = { ...me, isTwoFactorEnabled: val };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                // Force re-render if needed, or just let the user know
              }} />

              <div className="mt-8 pt-8 border-t border-gray-100">
                <h3 className="font-semibold mb-2">Update Credentials</h3>

                <form onSubmit={handleUpdateAdmin} className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm text-blue-800 mb-6">
                    <strong>Note:</strong> Updating your username or password will require you to login again immediately.
                  </div>

                  {/* Current Details Display */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Current Username</div>
                      <div className="text-gray-900 font-medium">{currentAdmin.username || "—"}</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Current Email</div>
                      <div className="text-gray-900 font-medium">{currentAdmin.email || "—"}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Username
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter new username (leave blank to keep current)"
                      value={adminSettings.username}
                      onChange={(e) => setAdminSettings({ ...adminSettings, username: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Email
                    </label>
                    <input
                      type="email"
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter new email (leave blank to keep current)"
                      value={adminSettings.email}
                      onChange={(e) => setAdminSettings({ ...adminSettings, email: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Password
                    </label>
                    <input
                      type="password"
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter new password (leave blank to keep current)"
                      value={adminSettings.password}
                      onChange={(e) => setAdminSettings({ ...adminSettings, password: e.target.value })}
                    />
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      Update Credentials
                    </button>
                  </div>
                </form>
              </div>
            </div>

          )
        }

        {/* REPORTS */}
        {
          active === "reports" && (
            <div className="space-y-8">
              {/* Summary Tiles */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-white border rounded-xl shadow-sm">
                  <div className="text-sm text-gray-500">Total Appraisals</div>
                  <div className="text-2xl font-semibold">{allAppraisals.length}</div>
                </div>
                <div className="p-4 bg-white border rounded-xl shadow-sm">
                  <div className="text-sm text-gray-500">Avg Faults / Appraisal</div>
                  <div className="text-2xl font-semibold">{avgFaultsPerAppraisal}</div>
                </div>
                <div className="p-4 bg-white border rounded-xl shadow-sm">
                  <div className="text-sm text-gray-500">Staff Registered</div>
                  <div className="text-2xl font-semibold">{staff.length}</div>
                </div>

                {/* NEW: Export tile */}
                <div className="p-4 bg-white border rounded-xl shadow-sm flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500">Export</div>
                    <div className="text-2xl font-semibold">CSV</div>
                  </div>
                  <button
                    onClick={handleExportCSV}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white"
                    title="Download appraisals CSV"
                  >
                    Export CSV
                  </button>
                </div>
              </div>


              {/* Bar Chart: Appraisals by Day (chartData reuse) */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Appraisals by Day</h3>
                  <div className="text-sm text-gray-500">Week overview</div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="appraisals" fill="#2563eb" />
                      <Bar dataKey="faults" fill="#059669" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Pie Chart: Fault vs Clean */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">Fault vs Clean Appraisals</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip />
                      <Legend />
                      <Pie
                        data={faultsPieData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={110}
                        label
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )
        }
        {
          active === "documents" && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <AdminDocumentsView selectedRegNumber={selectedRegNumber} />
            </div>
          )
        }

      </div >

      {/* ======= MODALS / OVERLAYS ======= */}

      {/* Approval Modal */}
      {
        approvalModalOpen && editingAppraisal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4">
                Review Appraisal - {editingAppraisal.reg}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approval Status
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setApprovalStatus('approved')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition ${approvalStatus === 'approved'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-green-400'
                      }`}
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() => setApprovalStatus('rejected')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition ${approvalStatus === 'rejected'
                      ? 'border-red-600 bg-red-50 text-red-700'
                      : 'border-gray-300 hover:border-red-400'
                      }`}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message to Staff (Optional)
                </label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Add any comments or feedback..."
                  value={adminMessage}
                  onChange={(e) => setAdminMessage(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                  onClick={() => {
                    setApprovalModalOpen(false);
                    setEditingAppraisal(null);
                    setApprovalStatus('');
                    setAdminMessage('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  onClick={handleApprovalSubmit}
                  disabled={!approvalStatus}
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        pdfPreviewUrl && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
              setPdfPreviewUrl(null);
              setPdfFor(null);
            }}
          >
            <div className="bg-white rounded-xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b">
                <div className="font-semibold">
                  {pdfFor?.reg ? `Document — ${pdfFor.reg}` : "Document"}
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1 rounded-lg border" onClick={() => pdfFor && generatePDF(pdfFor, { mode: "download" })}>
                    Download
                  </button>
                  <button
                    className="px-3 py-1 rounded-lg border"
                    onClick={() => {
                      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                      setPdfFor(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <iframe className="flex-1" src={pdfPreviewUrl} title="Document Viewer" />
            </div>
          </div>
        )
      }

      {
        editOpen && editingStaff && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Edit staff</h3>
              <label className="block mb-2">
                <span className="text-sm text-gray-600">First name</span>
                <input className="mt-1 w-full border rounded-lg px-3 py-2" value={editingStaff.firstName || ""} onChange={(e) => setEditingStaff({ ...editingStaff, firstName: e.target.value })} />
              </label>
              <label className="block mb-2">
                <span className="text-sm text-gray-600">Last name</span>
                <input className="mt-1 w-full border rounded-lg px-3 py-2" value={editingStaff.lastName || ""} onChange={(e) => setEditingStaff({ ...editingStaff, lastName: e.target.value })} />
              </label>
              <label className="block mb-2">
                <span className="text-sm text-gray-600">Username</span>
                <input className="mt-1 w-full border rounded-lg px-3 py-2" value={editingStaff.username || ""} onChange={(e) => setEditingStaff({ ...editingStaff, username: e.target.value })} />
              </label>
              <label className="block mb-4">
                <span className="text-sm text-gray-600">Email</span>
                <input type="email" className="mt-1 w-full border rounded-lg px-3 py-2" value={editingStaff.email || ""} onChange={(e) => setEditingStaff({ ...editingStaff, email: e.target.value })} />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white"
                  onClick={async () => {
                    try {
                      const fn = editingStaff.firstName?.trim() ?? "";
                      const ln = editingStaff.lastName?.trim() ?? "";
                      if (!fn && !ln) return alert("First name is required.");
                      if (!fn && ln) return alert("Enter first name.");
                      const payload = {
                        username: editingStaff.username?.trim(),
                        email: editingStaff.email?.trim(),
                        firstName: fn,
                        lastName: ln || null,
                      };
                      await api.updateStaff(editingStaff._id, payload);
                      setEditOpen(false);
                      setEditingStaff(null);
                      loadStaff();
                      setMsg("✅ Staff updated");
                    } catch (e) {
                      setMsg(e?.message || "❌ Failed to update staff");
                    }
                  }}
                >
                  Save
                </button>
                <button className="px-4 py-2 rounded-lg border" onClick={() => { setEditOpen(false); setEditingStaff(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        resetOpen && editingStaff && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold">Reset password for {[editingStaff.firstName, editingStaff.lastName].filter(Boolean).join(" ")}</h3>
              <div className="mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white"
                  onClick={async () => {
                    try {
                      const res = await api.resetStaffPassword(editingStaff._id); // expects { tempPassword }
                      if (res?.tempPassword) {
                        setTempPassword(res.tempPassword);
                        setMsg("✅ Temporary password generated");
                      } else {
                        setMsg("⚠️ No temp password returned from server");
                      }
                    } catch (e) {
                      setMsg(e?.message || "❌ Failed to reset password");
                    }
                  }}
                >
                  Auto-generate temporary password
                </button>

                <div className="mt-4">
                  <label className="block text-sm text-gray-600">Or set a new password</label>
                  <div className="mt-1 flex gap-2">
                    <input type="password" className="flex-1 border rounded-lg px-3 py-2" value={manualPw} onChange={(e) => setManualPw(e.target.value)} />
                    <button
                      className="px-4 py-2 rounded-lg border"
                      onClick={async () => {
                        const pw = manualPw.trim();
                        if (!pw) return setMsg("Enter a password");
                        try {
                          await api.setStaffPassword(editingStaff._id, { password: pw });
                          setMsg("✅ Password updated");
                          setResetOpen(false);
                          setEditingStaff(null);
                          setTempPassword("");
                          setManualPw("");
                        } catch (e) {
                          setMsg(e?.message || "❌ Failed to set password");
                        }
                      }}
                    >
                      Set password
                    </button>
                  </div>
                </div>

                {tempPassword && (
                  <div className="mt-4 p-3 border rounded-lg bg-white">
                    <strong>Temporary password (copy now):</strong>
                    <div className="mt-2 font-mono text-lg">{tempPassword}</div>
                    <small>This will be visible only once.</small>
                  </div>
                )}

                <div className="mt-4 text-right">
                  <button className="px-4 py-2 rounded-lg border" onClick={() => { setResetOpen(false); setEditingStaff(null); setTempPassword(""); }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* View Task Modal */}
      {
        viewTask && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Job Details - {viewTask.title}</h3>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase font-bold">Customer</label>
                  <div className="text-gray-900">{viewTask.customerName || 'N/A'}</div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase font-bold">Description</label>
                  <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                    {viewTask.description || 'No description provided.'}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-bold">Assigned To</label>
                    <div className="text-gray-900">
                      {viewTask.assignedTo?.firstName} {viewTask.assignedTo?.lastName}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-bold">Assigned By</label>
                    <div className="text-gray-900">
                      {viewTask.assignedBy?.username}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-bold">Status</label>
                    <div>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${viewTask.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        viewTask.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                        {viewTask.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-bold">Approval</label>
                    <div>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${viewTask.approvalStatus === 'Approved' ? 'bg-green-100 text-green-800' :
                        viewTask.approvalStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                        {viewTask.approvalStatus || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {viewTask.adminFeedback && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-bold">Admin Feedback</label>
                    <div className="text-gray-700 italic text-sm bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                      "{viewTask.adminFeedback}"
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                {viewTask.status === 'Completed' && (
                  <button
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => generateTaskPDF(viewTask)}
                  >
                    View Report PDF
                  </button>
                )}
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
                  onClick={() => setViewTask(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Review Task Modal */}
      {
        reviewTask && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Review Job - {reviewTask.title}</h3>

              <form onSubmit={submitTaskReview}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Status
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, status: 'Approved' })}
                      className={`flex-1 px-4 py-2 rounded-lg border-2 transition ${reviewForm.status === 'Approved'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-400'
                        }`}
                    >
                      ✓ Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, status: 'Rejected' })}
                      className={`flex-1 px-4 py-2 rounded-lg border-2 transition ${reviewForm.status === 'Rejected'
                        ? 'border-red-600 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-red-400'
                        }`}
                    >
                      ✗ Reject
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Feedback (Optional)
                  </label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Add any comments or feedback for the staff member..."
                    value={reviewForm.feedback}
                    onChange={(e) => setReviewForm({ ...reviewForm, feedback: e.target.value })}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                    onClick={() => setReviewTask(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Submit Review
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* EDIT TASK MODAL */}
      {
        editingTask && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingTask(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-amber-700 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
                <h2 className="text-xl font-bold">Edit Job</h2>
                <button onClick={() => setEditingTask(null)} className="text-white hover:bg-white/20 rounded-lg p-2">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateTask} className="p-6 space-y-4">
                {/* Reg Number */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-1">
                    Reg Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    value={editingTask.title}
                    onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                    required
                  />
                </div>

                {/* Customer Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">Customer Name</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      value={editingTask.customerName || ''}
                      onChange={(e) => setEditingTask({ ...editingTask, customerName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">Contact Number</label>
                    <input
                      type="tel"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      value={editingTask.customerContact || ''}
                      onChange={(e) => setEditingTask({ ...editingTask, customerContact: e.target.value })}
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-1">Customer Address</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    value={editingTask.customerAddress || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, customerAddress: e.target.value })}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-1">Description</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    value={editingTask.description || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  />
                </div>

                {/* Assign To with TBC */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-1">
                    Assign To <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                      value={editingTask.assignedTo === 'TBC' ? '' : editingTask.assignedTo}
                      onChange={(e) => setEditingTask({ ...editingTask, assignedTo: e.target.value })}
                      required={editingTask.assignedTo !== 'TBC'}
                      disabled={editingTask.assignedTo === 'TBC'}
                    >
                      <option value="">Select Staff...</option>
                      {staff.map(s => (
                        <option key={s._id} value={s._id}>
                          {[s.firstName, s.lastName].filter(Boolean).join(" ")}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setEditingTask({ ...editingTask, assignedTo: editingTask.assignedTo === 'TBC' ? '' : 'TBC' })}
                      className={`px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${editingTask.assignedTo === 'TBC'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      TBC
                    </button>
                  </div>
                </div>

                {/* Deadline (only if not TBC) */}
                {editingTask.assignedTo !== 'TBC' && (
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">Deadline</label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      value={editingTask.deadline || ''}
                      onChange={(e) => setEditingTask({ ...editingTask, deadline: e.target.value })}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    className="flex-1 bg-amber-600 text-white py-3 rounded-lg font-bold hover:bg-amber-700 transition-colors"
                  >
                    Update Job
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTask(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Toaster-ish inline message */}
      {
        msg && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white shadow-lg border rounded-full px-4 py-2 text-sm">
            {msg}
          </div>
        )
      }
    </div >
  );
}
